import { apiClient } from './client';
import type { Lote, LoteWithDetails, Activity, ActivityType, CostSummary, WeatherReport, LoteMovement } from '../types/api.types';

export interface LoteInput {
  ranchoId:  string;
  name:      string;
  cropType?: string;
  hectares?: number;
  latitude?: number;
  longitude?: number;
  /** #rrggbb elegido para el mapa; null (solo al editar) lo quita. */
  displayColor?: string | null;
  /** CAMPO (default al crear) o ADMINISTRATIVO. */
  uso?: 'CAMPO' | 'ADMINISTRATIVO';
}

export const fetchLotes = async (ranchoId?: string | null): Promise<Lote[]> =>
  (await apiClient.get('/api/lotes', { params: ranchoId ? { ranchoId } : undefined })).data;

export const createLote = async (input: Omit<LoteInput, 'displayColor'> & { displayColor?: string }): Promise<Lote> =>
  (await apiClient.post('/api/lotes', input)).data;

export const updateLote = async (loteId: string, input: Partial<Omit<LoteInput, 'ranchoId'>>): Promise<void> =>
  (await apiClient.put(`/api/lotes/${loteId}`, input)).data;

export const deleteLote = async (loteId: string): Promise<void> =>
  (await apiClient.delete(`/api/lotes/${loteId}`)).data;

export const fetchLote = async (loteId: string): Promise<LoteWithDetails> =>
  (await apiClient.get(`/api/lotes/${loteId}`)).data;

export interface LoteActivitiesFilters {
  type?:   string[];
  from?:   string;
  to?:     string;
  userId?: string;
  /** Zona o subzona (el servidor responde 404 si no es del lote) */
  moduleId?: string;
}

/** Filtros opcionales (FilterPanel, gap FieldData) — sin `filters`, comportamiento
 * idéntico al histórico (retrocompatible con el resto de callers existentes). */
export const fetchLoteActivities = async (loteId: string, filters?: LoteActivitiesFilters): Promise<Activity[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/activities`, {
    params: {
      ...(filters?.type?.length && { type: filters.type.join(',') }),
      ...(filters?.from && { from: filters.from }),
      ...(filters?.to && { to: filters.to }),
      ...(filters?.userId && { userId: filters.userId }),
      ...(filters?.moduleId && { moduleId: filters.moduleId }),
    },
  })).data;

/** Movimientos de bodega de un lote, a través de todas las bodegas
 * (gap FieldData — ver docs/superpowers/specs/2026-09-12-filterpanel-vista-lote-design.md). */
export const fetchLoteMovements = async (loteId: string): Promise<LoteMovement[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/movements`)).data;

export const fetchLoteCosts = async (loteId: string): Promise<CostSummary> =>
  (await apiClient.get(`/api/lotes/${loteId}/costs`)).data;

export const fetchLoteWeather = async (loteId: string): Promise<WeatherReport> =>
  (await apiClient.get(`/api/lotes/${loteId}/weather`)).data;

/** Recent activities across ALL lotes — used in Dashboard */
export const fetchRecentActivities = async (limit = 10, ranchoId?: string | null): Promise<Activity[]> =>
  (await apiClient.get('/api/activities/recent', { params: { limit, ...(ranchoId ? { ranchoId } : {}) } })).data;

export interface SearchActivitiesParams {
  q?: string;
  type?: ActivityType;
  loteId?: string;
  /** Solo mis registros ("Mis datos"); el servidor toma al autor del token. */
  mine?: boolean;
  /** ISO UTC: solo registros grabados después de este instante ("datos nuevos"). */
  createdAfter?: string;
  cursor?: string;
  limit?: number;
}

export interface SearchActivitiesResult {
  items: Activity[];
  nextCursor?: string;
}

/** Búsqueda paginada de actividades contra todo el historial del tenant (ActivityFeed) */
export const searchActivities = async (params: SearchActivitiesParams): Promise<SearchActivitiesResult> =>
  (await apiClient.get('/api/activities/search', { params })).data;

/** Reemplaza la nota libre de una actividad ya confirmada — nunca toca costos/materiales/compliance */
export const updateActivityNotes = async (loteId: string, activityId: string, notes: string): Promise<Activity> =>
  (await apiClient.patch(`/api/lotes/${loteId}/activities/${activityId}/notes`, { notes })).data;

/** Borrado lógico de una actividad: el servidor revierte el stock descontado y la oculta del feed. */
export const deleteActivity = async (loteId: string, activityId: string): Promise<void> => {
  await apiClient.delete(`/api/lotes/${loteId}/activities/${activityId}`);
};

export interface LoteSystemActivityItem {
  id: string;
  event: string;
  actor: string;
  actorName: string | null;
  at: string;
  metadata: { moduleCode?: string; name?: string; previousName?: string; areaHa?: number; changes?: string[] };
}

/** Cambios de sistema del lote: nombres, zonas, geocercas. */
export const fetchLoteSystemActivity = async (loteId: string): Promise<LoteSystemActivityItem[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/system-activity`)).data.items ?? [];
